import React, { memo, SVGProps, useMemo, useState } from 'react';

import classNames from 'classnames';
import { isCrawler, isNotEmpty } from 'utility/functions';
import SVG from 'react-inlinesvg';

import { toIconsEnum } from '.';
import type { IconProps, Storage } from './types';
import { Embedded, IconsStorageEnum, MAP, TS } from './types';

const indexing = isCrawler();

const decode = ({ url, kind }: Storage): string => {
  switch (kind) {
    case IconsStorageEnum.remote: {
      return `https://www.datocms-assets.com/${url}`;
    }
    // case IconsStorageEnum.local: { return `${process.env.NEXT_PUBLIC_BASE_URL}/${url}`; }
    default: {
      return '';
    }
  }
};

const Loader = ({ url, kind, iconId, className, ...oth }: IconProps & Storage): JSX.Element => {
  const [imageSrc] = useState(decode({ url, kind }));

  const { isSvg, isEmpty } = useMemo(() => {
    let isSvg = false;
    let isEmpty = true;

    if (indexing) {
      return {
        isSvg,
        isEmpty,
      };
    }

    if (isNotEmpty(imageSrc)) {
      isEmpty = false;
      isSvg = `${imageSrc}`.toLowerCase().endsWith('.svg');
    }

    return {
      isSvg,
      isEmpty,
    };
  }, [imageSrc, iconId]);

  if (!iconId) {
    return <React.Fragment key={`any-icon`} />;
  }

  const componentClassName = classNames('snai-image', `snai-image-${iconId}`, className ?? '');

  const objLoader = (
    <svg
      role="img"
      aria-label="fallback"
      className={`${componentClassName} snai-image-fallback`}
      {...(oth as SVGProps<SVGSVGElement>)}
      suppressHydrationWarning
    />
  );

  if (indexing || isEmpty) {
    return objLoader;
  }

  if (isSvg) {
    return (
      <SVG
        {...(oth as any)}
        uid={iconId}
        src={`${imageSrc}?ts=${TS}`}
        role="img"
        loader={objLoader}
        className={componentClassName}
      />
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
    <img
      {...(oth as any)}
      src={`${imageSrc}?ts=${TS}`}
      loading="lazy"
      decoding="async"
      className={componentClassName}
      suppressHydrationWarning
    />
  );
};

export const Icon = memo(
  function Icon({ iconId, ...props }: IconProps): JSX.Element {
    const { key, storage } = useMemo(() => {
      const key = toIconsEnum(iconId);
      const storage = MAP[key] ?? {};

      return { key, storage };
    }, [iconId]);

    switch (storage?.kind) {
      case IconsStorageEnum.embedded: {
        return <Embedded key={key} {...props} iconId={iconId} />;
      }
      default: {
        return <Loader key={key} iconId={iconId} url={storage?.url} kind={storage?.kind} {...props} />;
      }
    }
  },
  (prevProps, nextProps) => prevProps.iconId === nextProps.iconId
);
